/**
 * AttachmentPreviewOverlay — pre-send attachment chip rail.
 *
 * Backs Fix 2: when the composer has selected (but not yet sent)
 * attachments, render a horizontally-scrolling row of preview chips
 * that floats above the ThreadSwitcherBar (the threads band) and
 * below the composer input. Each chip shows the actual filename,
 * a mime-type icon (image thumbnail when applicable), the file
 * size, and a remove (×) button.
 *
 * Layout contract:
 *   • Renders inside <ChatFooter> as a position:absolute element
 *     so it visually overlaps the bottom edge of the threads band
 *     (z-index above the band, below modals).
 *   • Horizontal overflow scrolls; vertical layout is single-row.
 *   • Mobile: chip min-width drops, scroll behavior unchanged.
 *
 * The overlay also handles "placeholder" entries — attachments that
 * were persisted as draft metadata but whose blob died on full
 * refresh. These render with a "re-attach" affordance that fires
 * onPlaceholderClick (caller wires this to the file-picker).
 */

import React from 'react';
import {
  AttachmentPreviewOverlayContainer,
  AttachmentPreviewOverlayChip,
  AttachmentPreviewThumbnail,
  AttachmentPreviewMeta,
  AttachmentPreviewFilename,
  AttachmentPreviewSize,
  AttachmentPreviewRemoveButton,
  AttachmentPreviewPlaceholderChip,
} from './styles';
import { AttachmentIcon, ImageHoverWrapper } from './components';
import { formatFileSize, truncateFilename } from './utils';
import type { AttachmentDraftEntry } from './draftPersistence';

const FILENAME_TRUNCATE_LEN = 28;

export interface AttachmentPreviewPlaceholder {
  /** Name of the original file the placeholder represents. */
  name: string;
  type: string;
  size: number;
  /** Optional dataUrl for thumbnail when persisted under the 2 MB cap. */
  dataUrl?: string;
}

export interface AttachmentPreviewOverlayProps {
  /** Hydrated File objects currently selected on the composer. */
  files: File[];
  /**
   * Placeholder entries — attachments persisted as metadata but whose
   * blob did not survive a full reload. Render with a "re-attach"
   * affordance.
   */
  placeholders?: AttachmentPreviewPlaceholder[];
  /** Remove a hydrated file by index. */
  onRemoveFile: (index: number) => void;
  /** Remove a placeholder entry by index. */
  onRemovePlaceholder?: (index: number) => void;
  /** Click on a placeholder triggers the file picker for re-attach. */
  onPlaceholderClick?: (placeholder: AttachmentPreviewPlaceholder) => void;
}

/**
 * Render an image thumbnail for a File using URL.createObjectURL,
 * cleaning the object URL up on unmount to avoid blob leaks.
 */
const FileThumbnail: React.FC<{ file: File; alt: string }> = ({ file, alt }) => {
  const [src, setSrc] = React.useState<string | null>(null);
  React.useEffect(() => {
    let url: string | null = null;
    try {
      url = URL.createObjectURL(file);
      setSrc(url);
    } catch {
      setSrc(null);
    }
    return () => {
      if (url) {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }
    };
  }, [file]);
  if (!src) {
    return (
      <AttachmentPreviewThumbnail $placeholder>
        <AttachmentIcon fileType={file.type} isUser={false} />
      </AttachmentPreviewThumbnail>
    );
  }
  return (
    <ImageHoverWrapper src={src} alt={alt}>
      <AttachmentPreviewThumbnail>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
        />
      </AttachmentPreviewThumbnail>
    </ImageHoverWrapper>
  );
};

const PlaceholderThumbnail: React.FC<{ entry: AttachmentPreviewPlaceholder }> = ({ entry }) => {
  if (entry.dataUrl && entry.type.startsWith('image/')) {
    return (
      <ImageHoverWrapper src={entry.dataUrl} alt={entry.name}>
        <AttachmentPreviewThumbnail>
          <img src={entry.dataUrl} alt={entry.name} loading="lazy" decoding="async" />
        </AttachmentPreviewThumbnail>
      </ImageHoverWrapper>
    );
  }
  return (
    <AttachmentPreviewThumbnail $placeholder>
      <AttachmentIcon fileType={entry.type} isUser={false} />
    </AttachmentPreviewThumbnail>
  );
};

export const AttachmentPreviewOverlay: React.FC<AttachmentPreviewOverlayProps> = ({
  files,
  placeholders,
  onRemoveFile,
  onRemovePlaceholder,
  onPlaceholderClick,
}) => {
  const hasFiles = files.length > 0;
  const hasPlaceholders = !!placeholders && placeholders.length > 0;
  if (!hasFiles && !hasPlaceholders) return null;

  return (
    <AttachmentPreviewOverlayContainer
      role="list"
      aria-label="attachment preview"
    >
      {files.map((file, index) => {
        const isImage = (file.type || '').startsWith('image/');
        return (
          <AttachmentPreviewOverlayChip
            key={`file-${index}-${file.name}`}
            role="listitem"
            title={file.name}
          >
            {isImage ? (
              <FileThumbnail file={file} alt={file.name} />
            ) : (
              <AttachmentPreviewThumbnail $placeholder>
                <AttachmentIcon fileType={file.type} isUser={false} />
              </AttachmentPreviewThumbnail>
            )}
            <AttachmentPreviewMeta>
              <AttachmentPreviewFilename title={file.name}>
                {truncateFilename(file.name, FILENAME_TRUNCATE_LEN)}
              </AttachmentPreviewFilename>
              <AttachmentPreviewSize>
                {formatFileSize(file.size)} · {file.type || 'file'}
              </AttachmentPreviewSize>
            </AttachmentPreviewMeta>
            <AttachmentPreviewRemoveButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemoveFile(index);
              }}
              aria-label={`remove ${file.name}`}
              title={`remove ${file.name}`}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </AttachmentPreviewRemoveButton>
          </AttachmentPreviewOverlayChip>
        );
      })}

      {placeholders?.map((entry, index) => (
        <AttachmentPreviewPlaceholderChip
          key={`placeholder-${index}-${entry.name}`}
          role="listitem"
          title={`re-attach ${entry.name}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPlaceholderClick?.(entry);
          }}
        >
          <PlaceholderThumbnail entry={entry} />
          <AttachmentPreviewMeta>
            <AttachmentPreviewFilename title={entry.name}>
              {truncateFilename(entry.name, FILENAME_TRUNCATE_LEN)}
            </AttachmentPreviewFilename>
            <AttachmentPreviewSize>
              re-attach · {formatFileSize(entry.size)}
            </AttachmentPreviewSize>
          </AttachmentPreviewMeta>
          <AttachmentPreviewRemoveButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemovePlaceholder?.(index);
            }}
            aria-label={`dismiss ${entry.name}`}
            title={`dismiss ${entry.name}`}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </AttachmentPreviewRemoveButton>
        </AttachmentPreviewPlaceholderChip>
      ))}
    </AttachmentPreviewOverlayContainer>
  );
};

export type { AttachmentDraftEntry };
